/**
 * MessageList Component (Shared)
 *
 * Scrollable list of messages with date separators, infinite scroll,
 * auto-scroll to bottom, and date grouping.
 *
 * @module components/chat/MessageList
 */

import React, { useRef, useEffect, useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { format, isToday, isYesterday, isSameDay, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';
import MessageBubble from './MessageBubble';

const formatDateSeparator = (date) => {
  if (isToday(date)) return 'Hoy';
  if (isYesterday(date)) return 'Ayer';
  return format(date, "d 'de' MMMM 'de' yyyy", { locale: es });
};

const groupMessagesByDate = (messages) => {
  if (!messages || messages.length === 0) return [];

  const groups = [];
  let currentDate = null;
  let currentGroup = null;

  messages.forEach((message) => {
    const messageDate = parseISO(message.created_at);

    if (!currentDate || !isSameDay(currentDate, messageDate)) {
      currentDate = messageDate;
      currentGroup = { date: messageDate, messages: [] };
      groups.push(currentGroup);
    }

    currentGroup.messages.push(message);
  });

  return groups;
};

/**
 * Derive the "read" (double blue check) state for an own message.
 *
 * There is no per-message `is_read` column (see #446) — read tracking
 * lives on `chat_participants.last_read_at`. A message counts as read once
 * every OTHER participant's `last_read_at` is at or after the message's
 * `created_at`. With no other participants (e.g. data not loaded yet) it
 * is never marked read, matching the pre-fix visual (single gray check)
 * instead of showing a false positive.
 */
export const computeIsRead = (message, currentUserId, participants) => {
  if (!participants || participants.length === 0) return false;
  const others = participants.filter((p) => p.user_id !== currentUserId);
  if (others.length === 0) return false;
  return others.every((p) => {
    if (!p.last_read_at) return false;
    return new Date(p.last_read_at).getTime() >= new Date(message.created_at).getTime();
  });
};

const MessageList = ({
  messages = [],
  currentUserId,
  participants = [],
  loading = false,
  hasMore = false,
  onLoadMore,
  onImageClick,
  onReply,
  onShareToBitacora,
  onDelete,
  onEdit,
  canShareToBitacora = false,
}) => {
  const containerRef = useRef(null);
  const bottomRef = useRef(null);
  const [isNearBottom, setIsNearBottom] = useState(true);
  const [prevMessagesLength, setPrevMessagesLength] = useState(0);

  const checkScrollPosition = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;
    const threshold = 100;
    const isNear = container.scrollHeight - container.scrollTop - container.clientHeight < threshold;
    setIsNearBottom(isNear);
  }, []);

  const scrollToBottom = useCallback((smooth = true) => {
    if (bottomRef.current) {
      bottomRef.current.scrollIntoView({
        behavior: smooth ? 'smooth' : 'auto',
        block: 'end',
      });
    }
  }, []);

  const handleScroll = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;

    checkScrollPosition();

    if (container.scrollTop < 50 && hasMore && !loading && onLoadMore) {
      onLoadMore();
    }
  }, [hasMore, loading, onLoadMore, checkScrollPosition]);

  useEffect(() => {
    if (messages.length > prevMessagesLength) {
      if (isNearBottom || messages.length === 1) {
        scrollToBottom(messages.length > prevMessagesLength + 1);
      }
    }
    setPrevMessagesLength(messages.length);
  }, [messages.length, prevMessagesLength, isNearBottom, scrollToBottom]);

  useEffect(() => {
    if (messages.length > 0 && prevMessagesLength === 0) {
      scrollToBottom(false);
    }
  }, [messages.length, prevMessagesLength, scrollToBottom]);

  const groupedMessages = groupMessagesByDate(messages);

  const shouldShowAvatar = (message, index, groupMessages) => {
    if (message.sender_id === currentUserId) return false;
    if (message.message_type === 'system') return false;
    if (index === 0) return true;
    const prevMessage = groupMessages[index - 1];
    return prevMessage.sender_id !== message.sender_id;
  };

  const shouldShowName = (message, index, groupMessages) => {
    if (message.sender_id === currentUserId) return false;
    if (message.message_type === 'system') return false;
    if (index === 0) return true;
    const prevMessage = groupMessages[index - 1];
    return prevMessage.sender_id !== message.sender_id;
  };

  return (
    <div
      ref={containerRef}
      className="message-list-container"
      onScroll={handleScroll}
    >
      {loading && hasMore && (
        <div className="message-loading">
          <div className="message-loading-spinner" />
        </div>
      )}

      {!loading && messages.length === 0 && (
        <div className="message-empty">
          <i className="bi bi-chat-dots message-empty-icon" />
          <h4 className="message-empty-title">Sin mensajes</h4>
          <p className="message-empty-text">
            Sé el primero en escribir en esta conversación
          </p>
        </div>
      )}

      {groupedMessages.map((group) => (
        <div key={group.date.toISOString()}>
          <div className="date-separator">
            <span className="date-separator-badge">
              {formatDateSeparator(group.date)}
            </span>
          </div>

          {group.messages.map((message, messageIndex) => {
            const isOwn = message.sender_id === currentUserId;
            return (
              <MessageBubble
                key={message.id}
                message={message}
                isOwn={isOwn}
                isRead={isOwn ? computeIsRead(message, currentUserId, participants) : false}
                showAvatar={shouldShowAvatar(message, messageIndex, group.messages)}
                showName={shouldShowName(message, messageIndex, group.messages)}
                onImageClick={onImageClick}
                onReply={onReply}
                onShareToBitacora={onShareToBitacora}
                onDelete={onDelete}
                onEdit={onEdit}
                canShareToBitacora={canShareToBitacora}
              />
            );
          })}
        </div>
      ))}

      <div ref={bottomRef} style={{ height: 1 }} />
    </div>
  );
};

MessageList.propTypes = {
  messages: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      content: PropTypes.string,
      sender_id: PropTypes.string,
      created_at: PropTypes.string.isRequired,
      message_type: PropTypes.string,
      metadata: PropTypes.object,
      sender: PropTypes.object,
    })
  ),
  currentUserId: PropTypes.string,
  participants: PropTypes.arrayOf(
    PropTypes.shape({
      user_id: PropTypes.string,
      last_read_at: PropTypes.string,
    })
  ),
  loading: PropTypes.bool,
  hasMore: PropTypes.bool,
  onLoadMore: PropTypes.func,
  onImageClick: PropTypes.func,
  onReply: PropTypes.func,
  onShareToBitacora: PropTypes.func,
  onDelete: PropTypes.func,
  onEdit: PropTypes.func,
  canShareToBitacora: PropTypes.bool,
};

export default MessageList;
